import { CodeValueService } from '@common';
import { CodeValueGroups, PhxConstants } from '@common/model';

export interface WorkOrderTemplateFilterParams {
  listWorkOrderTemplates: any[];
  organizationIdClient: number;
  lineOfBusinessId: number;
  selectedProfileWorker: any;
  codeValueService: CodeValueService;
  currentTemplateId?: number;
}

export interface WorkOrderTemplateFilterResult {
  filteredTemplates: any[];
  shouldClearTemplate: boolean;
}

export interface WorkOrderWorkerFilterParams {
  allUserProfileWorkers: any[];
  organizationIdClient: number;
  templateId: number;
  selectedTemplate: any;
  codeValueService: CodeValueService;
}

export interface WorkOrderWorkerFilterResult {
  filteredWorkers: any[];
  shouldClearWorkerSelection: boolean;
}

export function filterWorkOrderTemplates(params: WorkOrderTemplateFilterParams): WorkOrderTemplateFilterResult {
  const {
    listWorkOrderTemplates,
    organizationIdClient,
    lineOfBusinessId,
    selectedProfileWorker,
    codeValueService,
    currentTemplateId
  } = params;

  const listFilteredWorkOrderTemplates = [];
  
  if (!listWorkOrderTemplates || !organizationIdClient) {
    return {
      filteredTemplates: listFilteredWorkOrderTemplates,
      shouldClearTemplate: currentTemplateId != null
    };
  }

  const isT4SalariedWorker = selectedProfileWorker?.T4TypeId === 
    codeValueService.getCodeValueIdByCode(PhxConstants.ProfileWorkerT4Type.T4Salaried, CodeValueGroups.CodeT4Type);

  listWorkOrderTemplates.forEach(entity => {
    // Filter templates based on T4 Salaried worker status
    let shouldIncludeTemplate = true;
    if (isT4SalariedWorker) {
      // T4 salaried workers should only see T4 Salaried Bill Side work order templates
      shouldIncludeTemplate = entity.Entity?.IsT4SalariedBillSideWorkOrder === true;
    } else {
      // Normal workers should not see T4 Salaried Bill Side work order templates
      shouldIncludeTemplate = entity.Entity?.IsT4SalariedBillSideWorkOrder !== true;
    }
    
    if (selectedProfileWorker && !shouldIncludeTemplate) {
      return;
    }

    entity.Entity.WorkOrders.forEach(versions => {
      if (versions.hasOwnProperty('WorkOrderVersions')) {
        versions.WorkOrderVersions.forEach(v => {
          if (v.LineOfBusinessId === lineOfBusinessId) {
            v.BillingInfoes.forEach(bi => {
              if (bi.OrganizationIdClient === organizationIdClient) {
                entity.DisplayValue = entity.Name + ' (' + entity.Description + ')';
                if (!listFilteredWorkOrderTemplates.includes(entity)) {
                  listFilteredWorkOrderTemplates.push(entity);
                }
              }
            });
          }
        });
      }
    });
  });

  const shouldClearTemplate = currentTemplateId != null && 
    !listFilteredWorkOrderTemplates.some(x => x.Id === currentTemplateId);

  return {
    filteredTemplates: listFilteredWorkOrderTemplates,
    shouldClearTemplate
  };
}

export function filterWorkOrderWorkers(params: WorkOrderWorkerFilterParams): WorkOrderWorkerFilterResult {
  const {
    allUserProfileWorkers,
    organizationIdClient,
    templateId,
    selectedTemplate,
    codeValueService
  } = params;

  // Return empty workers if no workers are available
  if (!allUserProfileWorkers || allUserProfileWorkers.length === 0) {
    return {
      filteredWorkers: [],
      shouldClearWorkerSelection: false
    };
  }

  // Reset workers when organizationIdClient is not selected
  if (!organizationIdClient) {
    return {
      filteredWorkers: [...allUserProfileWorkers],
      shouldClearWorkerSelection: true
    };
  }

  // We should see all workers when template is not selected
  if (!templateId) {
    return {
      filteredWorkers: [...allUserProfileWorkers],
      shouldClearWorkerSelection: false
    };
  }

  // Filter workers based on template T4 status
  if (selectedTemplate) {
    // Check if the selected template is a T4 Salaried Bill Side work order template
    const isT4SalariedBillSideTemplate = selectedTemplate.Entity?.IsT4SalariedBillSideWorkOrder === true;
    
    const t4SalariedCodeId = codeValueService.getCodeValueIdByCode(
      PhxConstants.ProfileWorkerT4Type.T4Salaried, 
      CodeValueGroups.CodeT4Type
    );

    if (isT4SalariedBillSideTemplate) {
      // T4 Salaried Bill Side template selected - only show T4 salaried workers
      return {
        filteredWorkers: allUserProfileWorkers.filter(worker => worker.T4TypeId === t4SalariedCodeId),
        shouldClearWorkerSelection: false
      };
    } else {
      // Non-T4 Salaried Bill Side template selected - don't show T4 salaried workers
      return {
        filteredWorkers:  allUserProfileWorkers.filter(worker => worker.T4TypeId !== t4SalariedCodeId),
        shouldClearWorkerSelection: false
      };
    }
  }

  // Putting this here for type safety, should not be hit, since assuming selectedTemplate.id == templateId
  return {
    filteredWorkers: [...allUserProfileWorkers],
    shouldClearWorkerSelection: false
  };
} 
